import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import { inRange } from "lodash-es";
import { MouseEvent, useState } from "react";

import { ShowMoreComponent } from "@/components/Search/SearchResults/components/ShowMore/showMore.component";
import { RecursiveHierarchicalFacet } from "@/components/Search/SearchResults/StateComponents/Facets/Facet/@types/facetType.types";
import { HIERARCHICAL_FACET_SHOWMORE_LIMIT } from "@/components/Search/SearchResults/StateComponents/Facets/Facet/facet.configuration";
import {
    extractValue,
    toHierarchicalFacet,
    toHierarchicalFacetPath
} from "@/services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";

type HierarchicalFacetComponentProps = {
    childValues: Record<string, RecursiveHierarchicalFacet>;
    selectedValue?: string;
    level: number;
    selectedValueLevel?: number;
    reset: () => Promise<void>;
    refine: (level: number, value: string) => void;
};

const HierarchicalFacetComponent = ({
    childValues,
    level,
    selectedValue,
    selectedValueLevel,
    reset,
    refine
}: HierarchicalFacetComponentProps) => {
    const [isShowingMore, setIsShowingMore] = useState(false);

    const [optimisticValue, setOptimisticValue] = useState<string | undefined>(
        selectedValue
    );

    const childEntries = Object.entries(childValues ?? {}).filter(
        ([_, childFacet]) => {
            if (!isDefined(selectedValue)) return true;

            const levelUp = toHierarchicalFacet(
                toHierarchicalFacetPath(selectedValue).slice(0, -1)
            );

            return (
                childFacet.facetValue.startsWith(levelUp) ||
                levelUp.startsWith(childFacet.facetValue)
            );
        }
    );

    const selectedValueIndex = childEntries.findIndex(([_, childFacet]) =>
        selectedValue?.startsWith(childFacet.facetValue)
    );

    const mustShowMore =
        selectedValueIndex >= HIERARCHICAL_FACET_SHOWMORE_LIMIT;
    const canShowMore =
        childEntries.length > HIERARCHICAL_FACET_SHOWMORE_LIMIT &&
        (!isDefined(selectedValueLevel) ||
            inRange(selectedValueLevel, level, level + 2));

    const childComponents = childEntries
        .map(([formattedValue, childFacet]) => {
            const childLevel = [...childFacet.facetValue.matchAll(/\s>\s/g)]
                .length;

            const isSelected = optimisticValue === childFacet.facetValue;
            const hasChildren = !!Object.keys(childFacet.childValues ?? {})
                .length;

            const handleOnClick = (e: MouseEvent) => {
                e.stopPropagation();

                // Select this category if not selected
                // Collapse this category if selected (collapsing = selecting parent category)
                if (isSelected) {
                    if (isDefined(childFacet.parentValue)) {
                        const parentFacet = childFacet.parentValue;

                        setOptimisticValue(parentFacet.facetValue);
                        refine(childLevel - 1, parentFacet.facetValue);
                    } else {
                        reset();
                    }
                } else {
                    setOptimisticValue(childFacet.facetValue);
                    refine(childLevel, childFacet.facetValue);
                }
            };

            const open =
                hasChildren && selectedValue?.startsWith(childFacet.facetValue);

            const { identifier, textValue } = extractValue(formattedValue);

            return (
                <li
                    key={identifier}
                    className={cn(
                        "tw:bg-transparent tw:py-0.5 tw:w-full tw:cursor-pointer tw:items-center"
                    )}
                    title={textValue}
                >
                    <button
                        type={"button"}
                        className={cn("tw:flex tw:items-center", {
                            "tw:font-bold": isSelected
                        })}
                        tabIndex={0}
                        onClick={handleOnClick}
                    >
                        <div
                            className={cn("tw:w-full tw:flex tw:items-center")}
                        >
                            {open && (
                                <i
                                    className={cn(
                                        "las la-angle-down",
                                        "tw:mr-1 tw:inline"
                                    )}
                                />
                            )}
                            {!open && (
                                <i
                                    className={cn(
                                        "las la-angle-right",
                                        "tw:mr-1 tw:inline"
                                    )}
                                />
                            )}
                            <div
                                className={cn(
                                    "tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis"
                                )}
                            >
                                {textValue}
                            </div>
                        </div>
                        <small className={cn("tw:pl-2 tw:inline")}>
                            ({childFacet.count})
                        </small>
                    </button>

                    {open && (
                        <HierarchicalFacetComponent
                            childValues={childFacet.childValues}
                            level={level + 1}
                            selectedValue={selectedValue}
                            selectedValueLevel={selectedValueLevel}
                            refine={refine}
                            reset={reset}
                        />
                    )}
                </li>
            );
        })
        .slice(
            0,
            mustShowMore || isShowingMore
                ? undefined
                : HIERARCHICAL_FACET_SHOWMORE_LIMIT
        );

    return (
        <>
            <ul
                className={cn("tw:mb-0", {
                    "tw:pl-4": level
                })}
            >
                {childComponents}
            </ul>
            {canShowMore && !mustShowMore && (
                <ShowMoreComponent
                    showMore={() => setIsShowingMore((prevState) => !prevState)}
                    isShowingMore={isShowingMore}
                    className={cn({ "tw:pl-4": level })}
                />
            )}
        </>
    );
};

export { HierarchicalFacetComponent };
